import { Button, Classes, InputGroup } from "@blueprintjs/core"
import { IconNames } from "@blueprintjs/icons"
import { useEffect, useState } from "react"
import Markdown from "react-markdown"
import { useWarrenChat } from "../../../hooks/useWarrenChat"
import {
  AssistantSectionsContainer,
  ErrorMessage,
  InputContainer,
  ProgressMessage,
  ResponseContainer,
  SectionContainer,
  TextContent,
  UserMessageBubble,
  UserMessageContainer,
  WarrenContainer,
} from "./Warren.styled"
import { DisplayMessage, ResponseSection } from "./types"
import TableSectionComponent from "./TableSection"

const Warren = () => {
  // Conversation history for backend (stateless backend needs full history)
  const [conversationHistory, setConversationHistory] = useState<Record<string, any> | null>(null)
  // Context for backend (stateless backend needs full context)
  const [contextHistory, setContextHistory] = useState<Record<string, any> | null>(null)
  // Display messages for UI (what user sees)
  const [displayMessages, setDisplayMessages] = useState<DisplayMessage[]>([])
  const [inputValue, setInputValue] = useState<string>("")

  // Use the streaming hook
  const {
    sendQuery,
    isStreaming,
    isComplete,
    sections,
    conversation,
    context,
    progressMessages,
    error: streamError,
    reset,
  } = useWarrenChat()

  // Effect to handle completed streams
  useEffect(() => {
    if (isComplete && sections && conversation) {
      // Update conversation history
      setConversationHistory(conversation)

      // Update context history
      setContextHistory(context || null)

      // Add assistant response to display
      setDisplayMessages(prev => [
        ...prev,
        {
          id: `assistant-${Date.now()}`,
          type: "assistant",
          sections,
        },
      ])

      // Reset the stream state for next message
      reset()
    }
  }, [isComplete, sections, conversation, context, reset])

  const handleSendMessage = () => {
    if (!inputValue.trim()) return

    const message = inputValue
    setInputValue("")

    const userMessageId = `user-${Date.now()}`

    // Immediately display user message (optimistic UI)
    setDisplayMessages(prev => [
      ...prev,
      {
        id: userMessageId,
        type: "user",
        content: message,
        queryKey: userMessageId,
      },
    ])

    // Send query with conversation history and context
    sendQuery({
      user_message: message,
      conversation:
        conversationHistory && conversationHistory.length > 0 ? conversationHistory : null,
      context: contextHistory,
    })
  }

  const sendButton = (
    <Button
      icon={IconNames.SEND_MESSAGE}
      onClick={handleSendMessage}
      disabled={!inputValue.trim() || isStreaming}
      loading={isStreaming}
      minimal
    />
  )

  const renderSection = (section: ResponseSection, index: number) => {
    if (section.type === "table") {
      return (
        <SectionContainer key={index}>
          <TableSectionComponent section={section} />
        </SectionContainer>
      )
    } else if (section.type === "text") {
      return (
        <SectionContainer key={index}>
          <TextContent>
            <Markdown>{section.text}</Markdown>
          </TextContent>
        </SectionContainer>
      )
    }
    return null
  }

  // Get the latest progress message
  const latestProgressMessage =
    progressMessages && progressMessages.length > 0
      ? progressMessages[progressMessages.length - 1]
      : null

  // Debug logging
  if (isStreaming || progressMessages.length > 0) {
    console.log("🎯 Warren render:", {
      isStreaming,
      progressMessages,
      latestProgressMessage,
      displayMessagesCount: displayMessages.length,
    })
  }

  return (
    <WarrenContainer>
      <ResponseContainer>
        {displayMessages.map((message, idx) => {
          if (message.type === "user") {
            // Show progress for the last user message when streaming
            const isLastUserMessage = idx === displayMessages.length - 1
            const showProgress = isLastUserMessage && isStreaming

            return (
              <div key={message.id}>
                <UserMessageContainer>
                  <UserMessageBubble>{message.content}</UserMessageBubble>
                </UserMessageContainer>
                {showProgress && latestProgressMessage && (
                  <ProgressMessage>{latestProgressMessage}</ProgressMessage>
                )}
                {showProgress && streamError && <ErrorMessage>{streamError}</ErrorMessage>}
              </div>
            )
          } else {
            return (
              <AssistantSectionsContainer key={message.id}>
                {message.sections?.map((section, index) => renderSection(section, index))}
              </AssistantSectionsContainer>
            )
          }
        })}
        {/* Show streaming progress if streaming and we have progress */}
        {isStreaming && latestProgressMessage && displayMessages.length === 0 && (
          <ProgressMessage>{latestProgressMessage}</ProgressMessage>
        )}
        {isStreaming && streamError && displayMessages.length === 0 && (
          <ErrorMessage>{streamError}</ErrorMessage>
        )}
      </ResponseContainer>
      <InputContainer>
        <InputGroup
          fill
          large
          round
          className={Classes.ROUND}
          placeholder="Ask Warren anything..."
          value={inputValue}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => setInputValue(e.target.value)}
          rightElement={sendButton}
          onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              handleSendMessage()
            }
          }}
        />
      </InputContainer>
    </WarrenContainer>
  )
}

export default Warren
